import { useTranslation } from 'react-i18next';
import {
  Button,
  FormControl,
  FormField,
  FormItem,
  NumberInput,
} from '@/shared/components';
import { cn } from '@/shared/utils';
import { formatReceivedAmount, getRequiredLabel } from '@/shared/lib';
import type { Control, FieldValues, Path } from 'react-hook-form';

interface AssetInfo {
  code: string;
  available: number;
  min?: number;
  precision?: number;
}

interface WithdrawAmountFormFieldProps<T extends FieldValues> {
  control: Control<T>;
  name: Path<T>;
  selectedAsset?: AssetInfo;
  minAmount?: number;
  onMaxClick: () => void;
  availableBalanceTranslationKey?: string;
  className?: string;
}

export function WithdrawAmountFormField<T extends FieldValues>({
  control,
  name,
  selectedAsset,
  minAmount,
  onMaxClick,
  availableBalanceTranslationKey = 'withdrawMethods.crypto.availableBalance',
  className,
}: WithdrawAmountFormFieldProps<T>) {
  const { t } = useTranslation();

  const placeholder =
    selectedAsset?.min !== undefined || minAmount !== undefined
      ? t('common.amountMinAmountPlaceholder', {
          min: formatReceivedAmount(minAmount ?? selectedAsset?.min ?? 0),
          currency: selectedAsset?.code || '',
        })
      : t('common.amountPlaceholder');

  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        return (
          <FormItem className={cn('flex flex-col gap-[0.4rem]', className)}>
            <FormControl>
              <NumberInput
                {...field}
                decimalScale={selectedAsset?.precision}
                isCustomElement
                value={field.value}
                type="text"
                inputMode="decimal"
                label={getRequiredLabel(placeholder)}
                onChange={field.onChange}
                className="p3"
                rightContent={
                  selectedAsset && (
                    <Button
                      type="button"
                      size="icon-button"
                      variant="icon"
                      onClick={onMaxClick}>
                      <span className="headline5 text-uppercase">
                        {t('withdrawMethods.crypto.maxButton')}
                      </span>
                    </Button>
                  )
                }
              />
            </FormControl>
            {selectedAsset && (
              <div
                className={cn(
                  'px-[1.6rem] text-muted-foreground p6',
                  fieldState.error && 'text-destructive'
                )}>
                {t(availableBalanceTranslationKey, {
                  available: formatReceivedAmount(selectedAsset.available),
                  code: selectedAsset.code,
                })}
              </div>
            )}
          </FormItem>
        );
      }}
    />
  );
}
